/**
 * MJPEG Streaming API Client
 * 
 * Much simpler than HLS - just returns a URL that can be used in an <img> tag
 */

import { api } from './api';

export interface MJPEGStreamStatus {
  ok: boolean;
  camera_id: string;
  running?: boolean;
  state?: string;
  device_id?: string;
  mjpeg_url?: string;
  rtsp_url?: string;
  started_at?: string;
  last_frame_at?: string;
  error?: string;
  message?: string;
  view_kind?: 'live' | 'annotated';
}

export interface MJPEGStreamList {
  ok: boolean;
  streams: Array<{
    camera_id: string;
    running: boolean;
    rtsp_url: string;
    started_at?: string;
    view_kind?: 'live' | 'annotated';
  }>;
}

/**
 * Snapshot probe result returned by the MJPEG snapshot endpoint.
 */
export interface MJPEGSnapshotResult {
  bytes: Uint8Array;
  isRealFrame: boolean;
}

export const MJPEG_MIN_REAL_SNAPSHOT_BYTES = 8192;
/** One real snapshot is enough to confirm ffmpeg decoded a frame (background probe only). */
export const MJPEG_LIVE_READY_SNAPSHOT_STREAK = 1;

export function isLikelyRealMjpegSnapshot(result: MJPEGSnapshotResult): boolean {
  return result.isRealFrame && result.bytes.length >= MJPEG_MIN_REAL_SNAPSHOT_BYTES;
}

/**
 * Fetch latest JPEG snapshot for a camera stream (used as a readiness probe).
 * The backend labels placeholders vs real frames via X-Edgentik-Frame.
 */
export async function getSnapshot(
  cameraId: string,
  edgeDeviceId?: string,
  viewKind?: 'live' | 'annotated'
): Promise<MJPEGSnapshotResult> {
  const params: Record<string, string> = {};
  const d = (edgeDeviceId || '').trim();
  if (d) params.edge_device_id = d;
  if (viewKind) params.view_kind = viewKind;
  const response = await api.get(`/api/v1/mjpeg/streams/${cameraId}/snapshot`, {
    responseType: 'arraybuffer',
    params: Object.keys(params).length ? params : undefined,
  });
  const bytes = new Uint8Array(response.data);
  const frameKind = String(response.headers?.['x-edgentik-frame'] || '').toLowerCase();
  return {
    bytes,
    isRealFrame: frameKind === 'real' || (!frameKind && bytes.length > 4),
  };
}

/**
 * Backward-compatible helper for code paths that only need the bytes.
 */
export async function getSnapshotBytes(
  cameraId: string,
  edgeDeviceId?: string,
  viewKind?: 'live' | 'annotated'
): Promise<Uint8Array> {
  const result = await getSnapshot(cameraId, edgeDeviceId, viewKind);
  return result.bytes;
}

/**
 * Start MJPEG stream for a camera
 */
export async function startStream(
  cameraId: string,
  rtspUrl?: string,
  edgeDeviceId?: string,
  mode?: 'stable' | 'low_latency',
  viewKind?: 'live' | 'annotated'
): Promise<MJPEGStreamStatus> {
  const body: Record<string, unknown> = {
    rtsp_url: rtspUrl,
    edge_device_id: edgeDeviceId,
    mode: mode || 'low_latency', // Default to low_latency for fast startup
  };
  if (viewKind) body.view_kind = viewKind;
  const response = await api.post(`/api/v1/mjpeg/streams/${cameraId}/start`, body);
  return response.data;
}

/**
 * Stop MJPEG stream for a camera
 */
/** Page ownership heartbeat. The multipart GET is not the durable viewer signal. */
export const PAGE_VIEWER_HEARTBEAT_MS = 15_000;

export async function heartbeatViewer(
  cameraId: string,
  viewerId: string,
  edgeDeviceId?: string,
  viewKind?: 'live' | 'annotated',
): Promise<void> {
  await api.post(
    `/api/v1/mjpeg/streams/${cameraId}/viewer`,
    {
      action: 'heartbeat',
      viewer_id: viewerId,
      edge_device_id: edgeDeviceId,
      view_kind: viewKind,
    },
    { params: { viewer_id: viewerId, view_kind: viewKind } },
  );
}

export async function releaseViewer(
  cameraId: string,
  viewerId: string,
  edgeDeviceId?: string,
  viewKind?: 'live' | 'annotated',
): Promise<void> {
  await api.post(
    `/api/v1/mjpeg/streams/${cameraId}/viewer`,
    {
      action: 'release',
      viewer_id: viewerId,
      edge_device_id: edgeDeviceId,
      view_kind: viewKind,
    },
    { params: { viewer_id: viewerId, view_kind: viewKind, action: 'release' } },
  );
}

export async function stopStream(
  cameraId: string,
  edgeDeviceId?: string,
  viewKind?: 'live' | 'annotated'
): Promise<MJPEGStreamStatus> {
  const d = (edgeDeviceId || '').trim();
  const body: Record<string, string> = d ? { edge_device_id: d } : {};
  if (viewKind) body.view_kind = viewKind;
  const response = await api.post(`/api/v1/mjpeg/streams/${cameraId}/stop`, body);
  return response.data;
}

/**
 * Get MJPEG stream status
 */
export async function getStreamStatus(
  cameraId: string,
  edgeDeviceId?: string,
  viewKind?: 'live' | 'annotated'
): Promise<MJPEGStreamStatus> {
  const d = (edgeDeviceId || '').trim();
  const params: Record<string, string> = d ? { edge_device_id: d } : {};
  if (viewKind) params.view_kind = viewKind;
  const response = await api.get(`/api/v1/mjpeg/streams/${cameraId}/status`, {
    params,
  });
  return response.data;
}

/**
 * List all active MJPEG streams
 */
export async function listStreams(): Promise<MJPEGStreamList> {
  const response = await api.get('/api/v1/mjpeg/streams');
  return response.data;
}

